/**
 * Tela de entrada. Fora da casca de propósito: quem não entrou não tem incorporadora,
 * não tem barra lateral e não tem estado de salvamento — mostrar a moldura vazia atrás
 * do formulário só sugeriria que existe algo ali.
 *
 * A entrada no Fincast é **só por convite**: não há "criar conta" aqui, e isso é dito na
 * tela para o usuário não ficar procurando o botão.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { entrar } from '../../../../packages/dados/src/sessao';

export function telaEntrada(aoEntrar: () => void): HTMLElement {
  const email = h('input', { class: 'input', type: 'email', autocomplete: 'username', placeholder: 'voce@empresa.com.br' });
  const senha = h('input', { class: 'input', type: 'password', autocomplete: 'current-password', placeholder: '••••••••' });
  const recado = h('div', { class: 'aviso', style: 'display:none' });
  const botao = h('button', { class: 'btn pri', style: 'width:100%;height:38px' }, 'Entrar');

  function dizer(msg: string): void {
    recado.textContent = msg;
    recado.style.display = '';
  }
  function calar(): void {
    recado.style.display = 'none';
  }

  async function submeter(): Promise<void> {
    calar();
    const e = email.value.trim();
    const s = senha.value;
    if (e === '' || s === '') {
      dizer('Informe o e-mail e a senha.');
      return;
    }
    botao.classList.add('dis');
    botao.textContent = 'Entrando…';
    try {
      await entrar(e, s);
      aoEntrar();
    } catch (erro) {
      dizer(erro instanceof Error ? erro.message : String(erro));
      senha.value = '';
      senha.focus();
    } finally {
      botao.classList.remove('dis');
      botao.textContent = 'Entrar';
    }
  }

  botao.addEventListener('click', () => void submeter());
  for (const campo of [email, senha]) {
    campo.addEventListener('keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') void submeter();
    });
    campo.addEventListener('input', calar);
  }

  /* O "ESQUECI MINHA SENHA" SAIU, e sai porque NAO FUNCIONA.
     O envio depende do SMTP embutido do Supabase, que recusa entregar para quem nao e
     da equipe do projeto — a documentacao dele diz, com todas as letras, que e "melhor
     esforco" e nao serve para producao. O botao mostrava mensagem de sucesso e o e-mail
     nunca chegava: falha silenciosa, que e o pior tipo.
     Volta no dia em que houver SMTP proprio (Resend, SES ou o servidor da Hype); ate la,
     quem esquece a senha pede ao gestor. Dizer isso e melhor que oferecer um caminho
     que termina em nada. */

  const cartao = h(
    'div',
    { class: 'card', style: 'width:min(390px,94vw);padding:30px;display:grid;gap:16px', 'data-el': 'EL-170' },
    h(
      'div',
      { class: 'brand', style: 'padding:0;font-size:var(--fs-lg);justify-content:center' },
      h('span', { class: 'logo' }, 'F'),
      h('span', {}, 'Fincast'),
    ),
    h('p', { class: 'desc', style: 'text-align:center;margin:-6px 0 2px' },
      'Viabilidade de financiamento à produção.'),
    recado,
    h('div', { class: 'field' }, h('label', { class: 'lbl' }, 'E-mail'), email),
    h('div', { class: 'field' }, h('label', { class: 'lbl' }, 'Senha'), senha),
    botao,
    h('p', { class: 'muted', style: 'text-align:center;font-size:var(--fs-2xs);line-height:1.5;margin:-4px 0 0' },
      'Esqueceu a senha? Peça ao gestor da sua incorporadora para redefinir o seu acesso.'),
    h('div', { class: 'sepln' }),
    h(
      'p',
      { class: 'muted', style: 'text-align:center;font-size:var(--fs-2xs);line-height:1.5;margin:0' },
      'O acesso ao Fincast é por convite. Se você ainda não tem, peça ao gestor da sua incorporadora.',
    ),
  );

  window.setTimeout(() => email.focus(), 0);

  return h(
    'div',
    { style: 'min-height:100vh;display:grid;place-items:center;padding:24px' },
    h('div', { style: 'display:grid;gap:18px;justify-items:center' }, cartao),
  );
}

/**
 * Autenticou, mas não existe perfil: o usuário está no login do Supabase e não em
 * `app.usuario`. Acontece quando o convite foi revogado ou o cadastro parou no meio —
 * é um estado real, e sem tela própria ele viraria uma lista vazia inexplicável.
 */
export function telaSemPerfil(email: string, aoSair: () => void): HTMLElement {
  return h(
    'div',
    { style: 'min-height:100vh;display:grid;place-items:center;padding:24px' },
    h(
      'div',
      { class: 'card', style: 'width:min(430px,94vw);padding:30px;display:grid;gap:14px;justify-items:center;text-align:center', 'data-el': 'EL-171' },
      h('span', { style: 'color:hsl(var(--warning))', html: icone.alerta(28) }),
      h('h3', { style: 'margin:0' }, 'Seu acesso ainda não foi liberado'),
      h(
        'p',
        { class: 'muted', style: 'margin:0;line-height:1.55' },
        `A conta ${email} entrou, mas ainda não está ligada a nenhuma incorporadora. ` +
          'Peça ao gestor para enviar (ou reenviar) o convite.',
      ),
      h('button', { class: 'btn out', onclick: aoSair }, 'Sair'),
    ),
  );
}
